// Client-side encryption utility for securing workflow data before network transmission
// This ensures raw hex function data never travels over the network unencrypted

import crypto from 'crypto-js';
import logger from './logger';

// SECURITY: Workflow key cache (memory only, never localStorage!)
let cachedWorkflowKey: string | null = null;
let keyFetchPromise: Promise<string> | null = null;

// Get encryption key from backend (securely delivered via session encryption)
async function getEncryptionKey(): Promise<string> {
    // Return cached key if available
    if (cachedWorkflowKey) {
        logger.log('✅ Using cached workflow encryption key');
        return cachedWorkflowKey;
    }

    // If a fetch is already in progress, wait for it
    if (keyFetchPromise) {
        logger.log('⏳ Workflow key fetch already in progress, waiting...');
        return keyFetchPromise;
    }

    // Start new fetch
    keyFetchPromise = (async () => {
        try {
            logger.log('🔑 Fetching workflow encryption key from backend...');

            // CRITICAL FIX: Ensure session key exists before fetching workflow key
            // Import sessionEncryption to generate session key if needed
            const { sessionEncryption } = await import('./sessionEncryption');

            // This will either return existing session or generate a new one
            const session = await sessionEncryption.getSessionKey();
            logger.log('✅ Session key obtained:', session.sessionId);

            const { sessionId, sessionKey } = session;

            // Import workflowStorageService dynamically to avoid circular dependencies
            const { workflowStorageService } = await import('../services/workflowStorageService');

            // Request workflow key from backend
            const response = await workflowStorageService.getWorkflowKey(sessionId);

            if (!response.success || !response.data?.encryptedKey) {
                throw new Error(response.error || 'Failed to retrieve workflow key');
            }

            logger.log('✅ Encrypted workflow key received from backend');

            // Decrypt the workflow key using session key
            const { encrypted, iv, authTag } = response.data.encryptedKey;

            // Use the session key we already have
            const sessionKeyBuffer = crypto.enc.Hex.parse(sessionKey);
            const ivBuffer = crypto.enc.Hex.parse(iv);

            // CRITICAL FIX: Convert hex-encoded ciphertext to CipherParams format for crypto-js
            // Backend sends hex string, but crypto-js needs CipherParams object
            const cipherParams = crypto.lib.CipherParams.create({
                ciphertext: crypto.enc.Hex.parse(encrypted)
            });

            // Decrypt using AES-256-CBC (compatible with backend)
            const decrypted = crypto.AES.decrypt(cipherParams, sessionKeyBuffer, {
                iv: ivBuffer,
                mode: crypto.mode.CBC,
                padding: crypto.pad.Pkcs7
            });

            const workflowKey = decrypted.toString(crypto.enc.Utf8);

            // Validate key format
            if (!workflowKey || workflowKey.length !== 64) {
                throw new Error(`Invalid workflow key format: expected 64 hex characters, got ${workflowKey?.length || 0}`);
            }

            // Cache in memory (NOT localStorage!)
            cachedWorkflowKey = workflowKey;
            logger.log('✅ Workflow encryption key cached in memory');

            return workflowKey;

        } catch (error) {
            logger.error('❌ Failed to fetch workflow encryption key:', error);
            throw new Error(`Workflow key fetch failed: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            keyFetchPromise = null;
        }
    })();

    return keyFetchPromise;
}

// Clear cached workflow key (call on logout)
export function clearWorkflowKey(): void {
    cachedWorkflowKey = null;
    keyFetchPromise = null;
    logger.log('🧹 Workflow encryption key cleared from memory');
}

// Encrypt sensitive data before sending to backend
export async function encryptForTransmission(data: any): Promise<{
    encrypted: string;
    iv: string;
    authTag: string;
}> {
    try {
        logger.log('🔐 Starting client-side encryption...');

        // Step 1: Get encryption key
        logger.log('📋 Step 1: Getting encryption key...');
        const encryptionKey = await getEncryptionKey();
        logger.log('✅ Encryption key obtained');

        // Step 2: Prepare data
        logger.log('📋 Step 2: Preparing data for encryption...');
        const dataString = typeof data === 'string' ? data : JSON.stringify(data);
        logger.log('✅ Data prepared, length:', dataString.length);

        // Step 3: Generate IV
        logger.log('📋 Step 3: Generating IV...');
        const iv = crypto.lib.WordArray.random(16);
        logger.log('✅ IV generated');

        // Step 4: Parse encryption key
        logger.log('📋 Step 4: Parsing encryption key...');
        const parsedKey = crypto.enc.Hex.parse(encryptionKey);
        logger.log('✅ Key parsed');

        // Step 5: Encrypt data
        logger.log('📋 Step 5: Encrypting data...');
        const encrypted = crypto.AES.encrypt(dataString, parsedKey, {
            iv: iv,
            mode: crypto.mode.CBC,
            padding: crypto.pad.Pkcs7
        });
        logger.log('✅ Data encrypted');

        // Step 6: Convert to strings
        logger.log('📋 Step 6: Converting to strings...');
        const result = {
            encrypted: encrypted.toString(),
            iv: iv.toString(crypto.enc.Hex),
            authTag: 'cbc-mode' // Indicate CBC mode to backend
        };
        logger.log('✅ Conversion complete');

        logger.log('🔐 Client-side encryption successful');
        return result;

    } catch (error) {
        // Enhanced error logging
        logger.error('❌ Client-side encryption failed with detailed error:');
        logger.error('Error type:', typeof error);
        logger.error('Error constructor:', error?.constructor?.name);
        logger.error('Error message:', error instanceof Error ? error.message : 'No message');
        logger.error('Error stack:', error instanceof Error ? error.stack : 'No stack');
        logger.error('Raw error object:', error);

        // Try to extract more details
        if (error && typeof error === 'object') {
            logger.error('Error keys:', Object.keys(error));
            logger.error('Error values:', Object.values(error));
        }

        const errorMessage = error instanceof Error ? error.message :
            (error && typeof error === 'object' && 'message' in error) ? String(error.message) :
                String(error);

        throw new Error(`Client encryption failed: ${errorMessage}`);
    }
}

// Decrypt data received from backend (if needed)
export async function decryptFromTransmission(encryptedData: {
    encrypted: string;
    iv: string;
    authTag: string;
}): Promise<string> {
    try {
        const encryptionKey = await getEncryptionKey();

        // CRITICAL FIX: Convert hex-encoded ciphertext to CipherParams format
        // Backend sends hex string, but crypto-js needs CipherParams object
        const cipherParams = crypto.lib.CipherParams.create({
            ciphertext: crypto.enc.Hex.parse(encryptedData.encrypted)
        });

        // Decrypt using AES-256-CBC (compatible with backend)
        const decrypted = crypto.AES.decrypt(cipherParams, crypto.enc.Hex.parse(encryptionKey), {
            iv: crypto.enc.Hex.parse(encryptedData.iv),
            mode: crypto.mode.CBC,
            padding: crypto.pad.Pkcs7
        });

        const decryptedString = decrypted.toString(crypto.enc.Utf8);

        if (!decryptedString) {
            throw new Error('Decryption resulted in empty string');
        }

        logger.log('🔓 Client-side decryption successful');
        return decryptedString;

    } catch (error) {
        logger.error('❌ Client-side decryption failed:', error);
        throw new Error(`Client decryption failed: ${error instanceof Error ? error.message : String(error)}`);
    }
}

// Environment detection utility - Maps deployment scripts to KV prefixes
function getEnvironment(): 'dev' | 'prod' | 'loc' {
    // Use hostname-based detection to distinguish between deployment environments
    if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;

        // Local development (.env.local → "loc" KV prefix)
        if (hostname === 'localhost') {
            return 'loc';
        }

        // Vercel production but dev environment (.env.fork → "dev" KV prefix)
        // This is ngrok tunnel back to Anvil fork
        if (hostname.includes('memer-operations-dev')) {
            return 'dev';
        }

        // True production (.env.prod → "prod" KV prefix)
        // This is Base mainnet deployment
        if (hostname.includes('memer-operations-prod')) {
            return 'prod';
        }
    }

    // Fallback: Use VITE_ENVIRONMENT as secondary indicator
    const viteEnv = import.meta.env.VITE_ENVIRONMENT;
    if (viteEnv === 'development') {
        return 'loc'; // Local development
    }

    // Default to 'dev' for production deployments that connect to Anvil
    return 'dev';
}

// Generate secure reference ID with environment-specific prefix
export function generateSecureReference(type: 'workflow' | 'template' | 'salt' = 'workflow'): string {
    const environment = getEnvironment();
    const timestamp = Date.now();
    const randomBytes = crypto.lib.WordArray.random(8).toString(crypto.enc.Hex);

    // Environment-specific prefixes (matches backend REDIS_PREFIXES)
    const prefixes = {
        dev: {
            workflow: 'dev_wf_',
            template: 'dev_tpl_',
            salt: 'dev_salt_'
        },
        prod: {
            workflow: 'prod_wf_',
            template: 'prod_tpl_',
            salt: 'prod_salt_'
        },
        loc: {
            workflow: 'loc_wf_',
            template: 'loc_tpl_',
            salt: 'loc_salt_'
        }
    };

    const baseId = `fb_${timestamp}_${randomBytes}`;
    const fullId = prefixes[environment][type] + baseId;

    logger.log(`🆔 Generated secure reference: ${fullId} (env: ${environment}, type: ${type})`);
    return fullId;
}

// Validate that data is properly encrypted before transmission
export function validateEncryptedPayload(payload: any): boolean {
    if (!payload || typeof payload !== 'object') {
        return false;
    }

    // Check that sensitive fields are encrypted
    const sensitiveFields = ['functionData', 'templateData'];

    for (const field of sensitiveFields) {
        if (payload[field]) {
            // If field exists, it should be encrypted (not raw hex or JSON)
            const value = payload[field];
            if (typeof value === 'string') {
                // Raw hex data starts with 0x - this should NOT be present
                if (value.startsWith('0x')) {
                    logger.error(`❌ Security violation: Raw hex data found in ${field}`);
                    return false;
                }
                // JSON strings containing functionData should NOT be present
                if (value.includes('functionData') && value.includes('0x')) {
                    logger.error(`❌ Security violation: Unencrypted JSON with hex data found in ${field}`);
                    return false;
                }
            }
        }
    }

    return true;
}
